import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Card, CardContent, Typography, Alert,
  LinearProgress, TextField, Grid,
} from '@mui/material';
import { VerifiedUser, GppBad, Search } from '@mui/icons-material';
import { verifyLedgerChain, clearChain } from '../../../store/slices/ledgerSlice';

const ChainVerification = () => {
  const dispatch = useDispatch();
  const { chain, loading } = useSelector(s => s.ledger);
  const [fromSeq, setFromSeq] = useState('');

  const handleVerify = () => {
    const params = {};
    if (fromSeq.trim()) params.from_sequence = fromSeq.trim();
    dispatch(verifyLedgerChain(params));
  };

  const handleClear = () => dispatch(clearChain());

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verify the SHA-256 hash chain integrity of all ledger entries. Each entry's hash is
        derived from its own data and the previous entry's hash, forming a tamper-evident chain.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <TextField
          label="Start from Sequence # (optional)"
          size="small"
          type="number"
          value={fromSeq}
          onChange={(e) => setFromSeq(e.target.value)}
          sx={{ width: 260 }}
          helperText="Leave blank to verify entire chain"
        />
        <Button
          variant="contained"
          startIcon={<Search />}
          onClick={handleVerify}
          disabled={loading.chain}
        >
          {loading.chain ? 'Verifying…' : 'Verify Chain'}
        </Button>
        {chain && (
          <Button variant="outlined" size="small" onClick={handleClear}>Clear</Button>
        )}
      </Box>

      {loading.chain && <LinearProgress sx={{ mb: 2 }} />}

      {chain && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderLeft: `4px solid ${chain.valid ? '#388e3c' : '#d32f2f'}` }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {chain.valid
                    ? <VerifiedUser sx={{ color: 'success.main' }} />
                    : <GppBad sx={{ color: 'error.main' }} />
                  }
                  <Typography variant="h6" fontWeight="bold" color={chain.valid ? 'success.main' : 'error.main'}>
                    {chain.valid ? 'VALID' : 'BROKEN'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Entries Verified</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {chain.entries_verified?.toLocaleString() ?? '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Verification Time</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {chain.verification_time_ms != null ? `${chain.verification_time_ms} ms` : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {!chain.valid && (
            <Grid item xs={12}>
              <Alert severity="error" icon={<GppBad />}>
                <Typography fontWeight="bold">Chain Break Detected</Typography>
                <Typography variant="body2">
                  First break at sequence #{chain.first_break_seq_no}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                  Reason: {chain.first_break_reason}
                </Typography>
              </Alert>
            </Grid>
          )}

          {chain.valid && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<VerifiedUser />}>
                All {chain.entries_verified?.toLocaleString()} entries verified successfully.
                The hash chain is intact and unmodified.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ChainVerification;
